"use strict";
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || function (mod) {
    if (mod && mod.__esModule) return mod;
    var result = {};
    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
    __setModuleDefault(result, mod);
    return result;
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.Tree = void 0;
/*---------------------------------------------------------------------------------------------
 * Copyright (c) Bentley Systems, Incorporated. All rights reserved.
 * See LICENSE.md in the project root for license terms and full copyright notice.
 *--------------------------------------------------------------------------------------------*/
const React = __importStar(require("react"));
const index_js_1 = require("../utils/index.js");
require("@itwin/itwinui-css/css/tree.css");
const classnames_1 = __importDefault(require("classnames"));
const TreeContext_js_1 = require("./TreeContext.js");
/**
 * Tree component used to display a hierarchical structure of `TreeNodes`.
 * User should control state of expanded, selected and disabled nodes using `getNode` prop.
 * @example
  type DemoData = {
    id: string;
    label: string;
    subItems: DemoData[];
  };

  const data: Array<DemoData> = [
    {
      id: 'Node-1',
      label: 'Facility 1',
      subItems: [{ id: 'Node-1-1', label: 'Unit 1', subItems: [] }],
    },
    {
      id: 'Node-2',
      label: 'Facility 2',
      subItems: [{ id: 'Node-2-1', label: 'Unit 2', subItems: [] }],
    },
  ];

  const [expandedNodes, setExpandedNodes] = React.useState<Record<string, boolean>>({});
  const onNodeExpanded = React.useCallback((nodeId: string, isExpanded: boolean) => {
    setExpandedNodes((oldExpanded) => ({ ...oldExpanded, [nodeId]: isExpanded }));
  }, []);

  const getNode = React.useCallback((node: DemoData): NodeData<DemoData> => {
    return {
      subNodes: node.subItems,
      nodeId: node.id,
      node,
      isExpanded: expandedNodes[node.id],
      hasSubNodes: node.subItems.length > 0,
    };
  }, [expandedNodes]);

  <Tree<DemoData>
    data={data}
    getNode={getNode}
    nodeRenderer={React.useCallback(({ node, ...rest }) => (
      <TreeNode
        label={node.label}
        onNodeExpanded={onNodeExpanded}
        {...rest}
      />
    ), [onNodeExpanded])}
  />
 */
const Tree = (props) => {
    const { data, className, nodeRenderer, getNode, enableVirtualization = false, style, ...rest } = props;
    (0, index_js_1.useTheme)();
    const treeRef = React.useRef(null);
    const focusedIndex = React.useRef(0);
    React.useEffect(() => {
        focusedIndex.current = 0;
    }, [data]);
    const getFocusableNodes = React.useCallback(() => {
        const focusableItems = (0, index_js_1.getFocusableElements)(treeRef.current);
        // Filter out focusable elements that are inside each node, e.g. checkbox
        return focusableItems.filter((i) => !focusableItems.some((p) => p.contains(i.parentElement)));
    }, []);
    const handleKeyDown = (event) => {
        if (event.altKey) {
            return;
        }
        const items = getFocusableNodes();
        if (!(items === null || items === void 0 ? void 0 : items.length)) {
            return;
        }
        const activeIndex = items.findIndex((el) => { var _a; return el.contains((_a = treeRef.current) === null || _a === void 0 ? void 0 : _a.ownerDocument.activeElement); });
        const currentIndex = activeIndex > -1 ? activeIndex : 0;
        switch (event.key) {
            case 'ArrowUp': {
                event.preventDefault();
                const newIndex = Math.max(0, currentIndex - 1);
                items[newIndex].focus();
                focusedIndex.current = newIndex;
                break;
            }
            case 'ArrowDown': {
                event.preventDefault();
                const newIndex = Math.min(items.length - 1, currentIndex + 1);
                items[newIndex].focus();
                focusedIndex.current = newIndex;
                break;
            }
            default:
                break;
        }
    };
    const [flatNodesList, firstLevelNodesList] = React.useMemo(() => {
        const flatList = [];
        const firstLevelNodes = [];
        const flattenNodes = (nodes = [], depth = 0, parentNode) => {
            const nodeIdList = Array();
            nodes.forEach((element, index) => {
                const { subNodes, ...nodeProps } = getNode(element);
                const flatNode = {
                    nodeProps,
                    depth,
                    parentNode,
                    indexInGroup: index,
                };
                nodeIdList.push(flatNode.nodeProps.nodeId);
                flatList.push(flatNode);
                if (depth === 0) {
                    firstLevelNodes.push(flatNode);
                }
                if (flatNode.nodeProps.isExpanded) {
                    const subNodeIds = flattenNodes(subNodes, depth + 1, flatNode);
                    flatNode.subNodeIds = subNodeIds;
                }
            });
            return nodeIdList;
        };
        flattenNodes(data);
        return [flatList, firstLevelNodes];
    }, [data, getNode]);
    const itemRenderer = React.useCallback((index) => {
        var _a, _b, _c, _d;
        const node = flatNodesList[index];
        return (React.createElement(TreeContext_js_1.TreeContext.Provider, { key: node.nodeProps.nodeId, value: {
                nodeDepth: node.depth,
                subNodeIds: node.subNodeIds,
                groupSize: node.depth === 0
                    ? firstLevelNodesList.length
                    : (_c = (_b = (_a = node.parentNode) === null || _a === void 0 ? void 0 : _a.subNodeIds) === null || _b === void 0 ? void 0 : _b.length) !== null && _c !== void 0 ? _c : 0,
                indexInGroup: node.indexInGroup,
                parentNodeId: (_d = node.parentNode) === null || _d === void 0 ? void 0 : _d.nodeProps.nodeId,
                scrollToParent: node.parentNode
                    ? () => {
                        var _a;
                        const parentNodeId = (_a = node.parentNode) === null || _a === void 0 ? void 0 : _a.nodeProps.nodeId;
                        const parentNodeIndex = flatNodesList.findIndex((n) => n.nodeProps.nodeId === parentNodeId);
                        setScrollToIndex(parentNodeIndex);
                    }
                    : undefined,
            } }, nodeRenderer(node.nodeProps)));
    }, [firstLevelNodesList.length, flatNodesList, nodeRenderer]);
    const [scrollToIndex, setScrollToIndex] = React.useState();
    const flatNodesListRef = React.useRef(flatNodesList);
    React.useEffect(() => {
        flatNodesListRef.current = flatNodesList;
    }, [flatNodesList]);
    React.useEffect(() => {
        setTimeout(() => {
            var _a;
            if (scrollToIndex !== undefined) {
                const nodeId = flatNodesListRef.current[scrollToIndex].nodeProps.nodeId;
                const nodeElement = (_a = treeRef.current) === null || _a === void 0 ? void 0 : _a.ownerDocument.querySelector(`#${nodeId}`);
                nodeElement === null || nodeElement === void 0 ? void 0 : nodeElement.focus();
                // Need to reset that if navigating with mouse and keyboard,
                // e.g. pressing arrow left to go to parent node and then with mouse
                // clicking some other child node and then pressing arrow left
                setScrollToIndex(undefined);
            }
        });
    }, [scrollToIndex]);
    const handleFocus = (event) => {
        var _a, _b;
        if ((_a = treeRef.current) === null || _a === void 0 ? void 0 : _a.contains(event.relatedTarget)) {
            return;
        }
        const items = getFocusableNodes();
        if (items.length > 0) {
            (_b = items[focusedIndex.current]) === null || _b === void 0 ? void 0 : _b.focus();
        }
    };
    return (React.createElement(React.Fragment, null, enableVirtualization ? (React.createElement(VirtualizedTree, { flatNodesList: flatNodesList, itemRenderer: itemRenderer, scrollToIndex: scrollToIndex, onFocus: handleFocus, onKeyDown: handleKeyDown, ref: treeRef, className: className, style: style, ...rest })) : (React.createElement(TreeElement, { onKeyDown: handleKeyDown, onFocus: handleFocus, className: className, style: style, ref: treeRef, ...rest }, flatNodesList.map((_, i) => itemRenderer(i))))));
};
exports.Tree = Tree;
const TreeElement = React.forwardRef(({ children, className, ...rest }, ref) => {
    return (React.createElement("ul", { className: (0, classnames_1.default)('iui-tree', className), role: 'tree', ref: ref, tabIndex: 0, ...rest }, children));
});
// Having virtualized tree separately prevents from running all virtualization logic
const VirtualizedTree = React.forwardRef(({ flatNodesList, itemRenderer, scrollToIndex, className, style, ...rest }, ref) => {
    const { outerProps, innerProps, visibleChildren } = (0, index_js_1.useVirtualization)({
        itemsLength: flatNodesList.length,
        itemRenderer: itemRenderer,
        scrollToIndex,
    });
    return (React.createElement("div", { ...{
            ...outerProps,
            className: (0, classnames_1.default)(className, outerProps.className),
            style: { ...style, ...outerProps.style },
        } },
        React.createElement(TreeElement, { ...innerProps, ...rest, ref: (0, index_js_1.mergeRefs)(ref, innerProps.ref) }, visibleChildren)));
});
exports.default = exports.Tree;
